import { CommonModule } from '@angular/common';
import { Component, DestroyRef, HostBinding, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { QuoteLeadOutputV5 } from '@lx/quote-tool/shared/types';
import { GoogleAnalyticsClickEventDirective, GoogleAnalyticsService } from '@lx/shared/google-analytics';
import { ButtonComponent } from '@lx/shared/ui/button';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { exhaustMap, Observable, take, tap } from 'rxjs';
import { GoogleAnalyticsEventEnum } from '../../google-analytics';
import { LeadService, NavigationService } from '../../services';
import { QuoteStore, QuoteToolLiteStore } from '../../store';
import { KeyboardModeService } from '@lx/shared/ui/keyboard-focus';
import { Flows, Flow } from '@lx/quote-tool/lite/shared/types';

@Component({
  standalone: true,
  selector: 'lx-quote-step-footer',
  templateUrl: './quote-step-footer.component.html',
  styleUrls: ['./quote-step-footer.component.scss'],
  imports: [ButtonComponent, ConfirmDialogModule, CommonModule, GoogleAnalyticsClickEventDirective],
})
export class QuoteStepFooterComponent implements OnInit {
  @HostBinding('class.keyboard-navigation') protected withKeyboardNavigation = false;
  sendMyQuoteIconButton = '';
  sendMyQuoteTextButton = '';
  sendMyQuoteDisabledButton = false;
  activeFlow$: Observable<Flow> | undefined;

  createAnotherQuoteEventName = GoogleAnalyticsEventEnum.QuoteCreateAnother;
  sendMyQuoteEventName = GoogleAnalyticsEventEnum.QuoteEmail;

  protected readonly Flows = Flows;

  private readonly qtLiteStore = inject(QuoteToolLiteStore);
  private readonly quoteStore = inject(QuoteStore);
  private readonly navigationService = inject(NavigationService);
  private readonly leadService = inject(LeadService);
  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);

  private readonly confirmationService = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly keyboardModeService = inject(KeyboardModeService);

  ngOnInit(): void {
    this.activeFlow$ = this.qtLiteStore.activeFlow$;
    this.qtLiteStore.selectionForQuotePrices$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((selection) => {
      if (selection) {
        this.setDefaultSendMyQuoteButtonState();
      }
    });

    this.keyboardModeService.isKeyboardMode$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((keyboardModeEnabled) => {
      this.withKeyboardNavigation = keyboardModeEnabled;
    });
  }

  showStartAgainConfirmationDialog(event: Event) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: $localize`Are you sure that you want to proceed?`,
      icon: 'pi pi-exclamation-triangle',
      ...this.getStartAgainAcceptButtonConfig(),
      ...this.getStartAgainRejectButtonConfig(),
    });
  }

  sendQuote() {
    this.qtLiteStore.selectionForLeads$
      .pipe(
        take(1),
        tap(() =>
          this.setSendMyQuoteButtonState({
            icon: 'pi pi-spin pi-spinner',
            text: $localize`Sending...`,
            disabled: true,
          }),
        ),
        exhaustMap((state) => this.leadService.createLead(state)),
        tap((out: QuoteLeadOutputV5) => {
          this.qtLiteStore.updateState({ quoteId: out?.quoteId });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.setSendMyQuoteButtonState({
            icon: 'pi pi-check',
            text: $localize`Sent !`,
            disabled: true,
          });
        },
        error: () => {
          this.setDefaultSendMyQuoteButtonState();
        },
      });
  }

  private startAgain(): void {
    this.navigationService.navigateToFirstStep(() => {
      this.qtLiteStore.resetQuote();
      this.qtLiteStore.incrementRequestedQuotes();
      this.quoteStore.resetState();
    });
  }

  private getStartAgainAcceptButtonConfig() {
    return {
      acceptIcon: 'none',
      acceptLabel: $localize`Yes`,
      acceptButtonStyleClass: 'quote-step-footer__start-again-confirmation-dialog__accept-button',
      accept: () => {
        this.googleAnalyticsService.trackClickEvent(GoogleAnalyticsEventEnum.QuoteCreateAnotherAccept);
        this.startAgain();
      },
    };
  }

  private getStartAgainRejectButtonConfig() {
    return {
      rejectLabel: $localize`No`,
      rejectIcon: 'none',
      rejectButtonStyleClass: 'quote-step-footer__start-again-confirmation-dialog__reject-button lx-button--primary',
      reject: () => {
        this.googleAnalyticsService.trackClickEvent(GoogleAnalyticsEventEnum.QuoteCreateAnotherReject);
      },
    };
  }

  private setDefaultSendMyQuoteButtonState() {
    this.setSendMyQuoteButtonState({
      icon: '',
      text: $localize`Send my quote`,
      disabled: false,
    });
  }

  private setSendMyQuoteButtonState({ text, icon, disabled }: { text: string; icon: string; disabled: boolean }) {
    this.sendMyQuoteTextButton = text;
    this.sendMyQuoteIconButton = icon;
    this.sendMyQuoteDisabledButton = disabled;
  }
}
